import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Pagination, PAGINATION_PAGE_SIZE } from '@/components/Pagination';
import { usePartner } from '@/hooks/use-partner';
import { PartnerTableHeader } from './PartnerTableHeader';
import { PartnerTable } from './PartnerTable';
import { Button } from '@/components/ui/button';
import { Partner } from '@/types/partner';
import BrandWizard from '@/pages/settings/content/brands-sectors/create';
import { PermissionGuard } from '@/components/PermissionGuard';

export default function PartnersPage() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { partners, total, loading: isLoading, error, refetch } = usePartner({
    offset: (currentPage - 1) * PAGINATION_PAGE_SIZE,
    limit: PAGINATION_PAGE_SIZE,
    q: debouncedSearchTerm || undefined,
  });

  const handleSelectPartner = (partner: Partner) => {
    navigate(`/app/settings/partners/${partner.id}/clients`);
  };

  return (
    <div className="w-full">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <PartnerTableHeader
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
        />
        <PermissionGuard action="create_partners">
          <Button
            type="button"
            className="flex items-center gap-2 bg-black text-white hover:bg-gray-900 shrink-0"
            onClick={() => setIsCreateOpen(true)}
          >
            <Plus className="w-4 h-4" />
            Nova marca
          </Button>
        </PermissionGuard>
      </div>
      <div className="rounded-lg overflow-hidden w-full mt-12 mb-6">
        <PartnerTable
          partners={partners}
          isLoading={isLoading}
          error={error}
          onSelectPartner={handleSelectPartner}
        />
      </div>
      {!isLoading && total > 0 && (
        <Pagination
          currentPage={currentPage}
          totalItems={total}
          onPageChange={setCurrentPage}
        />
      )}
      <BrandWizard
        open={isCreateOpen}
        onOpenChange={setIsCreateOpen}
        onSuccess={() => refetch()}
      />
    </div>
  );
}
